import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage, ImageUploadField } from "@tellescope/ui";
import { useSecureFileUrl } from "@tellescope/tanstack-query";
import { formatInitials } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import type { Enduser } from "@tellescope/types-models";

interface AvatarUploadProps {
  enduser: Enduser;
  onUpload: (file: File) => Promise<string>;
}

export const AvatarUpload = ({ enduser, onUpload }: AvatarUploadProps) => {
  const { t } = useTranslation("contacts");
  // Track the in-session URL so the avatar preview updates immediately after upload
  // without waiting for the useSecureFileUrl query to revalidate.
  const [localAvatarUrl, setLocalAvatarUrl] = useState<string | undefined>(undefined);
  const persistedAvatarUrl = useSecureFileUrl(enduser.avatar);
  const avatarUrl = localAvatarUrl ?? persistedAvatarUrl;

  const handleChange = (url: string) => {
    setLocalAvatarUrl(url || undefined);
  };

  return (
    <div className="flex items-center gap-4">
      <Avatar className="h-32 w-32 shrink-0">
        <AvatarImage src={avatarUrl} />
        <AvatarFallback useStaticBackground id={enduser.id} className="text-2xl">
          {formatInitials(enduser)}
        </AvatarFallback>
      </Avatar>
      <ImageUploadField
        value=""
        onChange={handleChange}
        onUpload={onUpload}
        accept="image/jpeg,image/png,image/webp,image/gif"
        uploadLabel={avatarUrl ? t("Change Photo") : t("Upload Photo")}
        uploadingLabel={t("Uploading...")}
      />
    </div>
  );
};
